import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUser, FiCalendar, FiUserPlus } = FiIcons;

const AddChild = () => {
  const navigate = useNavigate();
  const { addChild } = useApp();
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    avatarEmoji: '😊'
  });
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);

  const emojiOptions = [
    '😊', '😄', '🥰', '😎', '🤗', '🌟',
    '😍', '🤔', '😇', '🤓', '😃', '🤪',
    '🐱', '🐶', '🐸', '🦁', '🐼', '🐨',
    '🦄', '🐸', '🦊', '🐯', '🐮', '🐷',
    '🦋', '🐛', '🐙', '🦀', '🐠', '🐳',
    '🌈', '🌟', '⭐', '🌙', '☀️', '⚡',
    '🎈', '🎁', '🎮', '⚽', '🏀', '🎨',
    '🚀', '🌍', '🎯', '🏆', '🎪', '🎭',
    '✨', '🍀', '🔥', '💎', '🎊', '🎉'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.age) return;
    
    const childData = {
      name: formData.name.trim(),
      age: parseInt(formData.age),
      avatarEmoji: formData.avatarEmoji
    };
    
    addChild(childData);
    navigate('/');
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Add New Child</h1>
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Add New Child</h2>
            <p className="text-gray-600">Create a profile to start tracking habits</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Child's Name
              </label>
              <div className="relative">
                <SafeIcon 
                  icon={FiUser} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
                />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-white border border-gray-300 rounded-lg pl-10 pr-4 py-3 text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter child's name"
                  required
                />
              </div>
            </div>

            {/* Age Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Age
              </label>
              <div className="relative">
                <SafeIcon 
                  icon={FiCalendar} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
                />
                <input
                  type="number"
                  min="1"
                  max="18"
                  value={formData.age}
                  onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                  className="w-full bg-white border border-gray-300 rounded-lg pl-10 pr-4 py-3 text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter age"
                  required
                />
              </div>
            </div>

            {/* Avatar Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Choose Avatar
              </label>
              <div className="flex flex-col items-center space-y-4">
                <button
                  type="button"
                  onClick={() => setShowAvatarPicker(true)}
                  className="w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-3xl hover:scale-105 transition-transform"
                >
                  {formData.avatarEmoji}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAvatarPicker(true)}
                  className="text-purple-600 hover:text-purple-700 transition-colors font-medium"
                >
                  Select Avatar
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 bg-transparent border border-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!formData.name.trim() || !formData.age}
                className="flex-1 bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiUserPlus} />
                <span>Add Child</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Avatar Picker Modal */}
      {showAvatarPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg border border-gray-200">
            <h3 className="text-xl font-bold text-gray-800 text-center mb-6">Choose Avatar</h3>
            
            {/* Tab Buttons */}
            <div className="flex mb-6">
              <button className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-l-lg flex items-center justify-center space-x-2">
                <span>😊</span>
                <span>Emojis</span>
              </button>
              <button className="flex-1 bg-gray-200 text-gray-500 py-2 px-4 rounded-r-lg flex items-center justify-center space-x-2 cursor-not-allowed">
                <span>📁</span>
                <span>Upload</span>
              </button>
            </div>

            {/* Emoji Grid */}
            <div className="grid grid-cols-6 gap-3 mb-6 max-h-80 overflow-y-auto">
              {emojiOptions.map((emoji, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, avatarEmoji: emoji }));
                    setShowAvatarPicker(false);
                  }}
                  className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl transition-colors hover:bg-purple-50 ${
                    formData.avatarEmoji === emoji ? 'bg-purple-100 ring-2 ring-purple-500' : 'bg-gray-50'
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>

            {/* Close Button */}
            <div className="text-center">
              <button
                onClick={() => setShowAvatarPicker(false)}
                className="bg-gray-500 text-white py-2 px-6 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddChild;